import { useEffect, useState } from 'react'
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import Button from '@mui/material/Button'
import { enqueueSnackbar } from 'notistack'
import API from '@enums/api'
import { PodiumFleetRow } from '@types'

export type PodiumAction = 'disable' | 'enable' | 'wipe'

/**
 * Only the identity fields are needed to confirm and send a command, so the detail page
 * can open this dialog with its own payload rather than a fleet row.
 */
export type PodiumActionTarget = Pick<
    PodiumFleetRow,
    'id' | 'name' | 'hardware_id' | 'org_name'
>

interface Props {
    action: PodiumAction | null
    podium: PodiumActionTarget | null
    onClose: () => void
    /** Called after a successful command so the caller can refetch the fleet. */
    onConfirmed: () => void
}

/**
 * Best human-readable message from a failed request: prefer the server's own message
 * (`result`/`message`), then the generic Error, then the HTTP status.
 */
function errorMessage(res: {
    error?: unknown
    result?: unknown
    message?: unknown
    status?: number
}): string {
    if (typeof res.result === 'string') return res.result
    if (typeof res.message === 'string') return res.message
    if (res.error instanceof Error) return res.error.message
    return `status ${res.status ?? 'unknown'}`
}

const ACTION_TEXT: Record<
    PodiumAction,
    {
        title: string
        body: string
        confirmLabel: string
        confirmColor: 'primary' | 'warning' | 'error'
        past: string
    }
> = {
    disable: {
        title: 'Disable podium',
        body: 'The device stops recording but stays reachable, so it can be re-enabled later. No data is deleted.',
        confirmLabel: 'Disable',
        confirmColor: 'warning',
        past: 'Disabled',
    },
    enable: {
        title: 'Enable podium',
        body: 'The device is re-enabled and returns to standby once it next reports in.',
        confirmLabel: 'Enable',
        confirmColor: 'primary',
        past: 'Enabled',
    },
    wipe: {
        title: 'Wipe podium',
        body: 'This factory-wipes the device and revokes its certificate once it confirms. This is irreversible — the device must be re-onboarded from scratch afterwards.',
        confirmLabel: 'Wipe device',
        confirmColor: 'error',
        past: 'Wipe requested for',
    },
}

export default function PodiumActionDialog({
    action,
    podium,
    onClose,
    onConfirmed,
}: Props) {
    const [loading, setLoading] = useState(false)
    const [confirmText, setConfirmText] = useState('')

    // Reset the type-to-confirm field whenever the dialog target changes.
    useEffect(() => {
        setConfirmText('')
    }, [action, podium?.id])

    const open = action !== null && podium !== null
    if (!open || !action || !podium) return null

    const text = ACTION_TEXT[action]
    const label = podium.name || podium.hardware_id || 'this podium'
    // Wipe is gated behind typing the exact hardware ID.
    const wipeReady =
        action !== 'wipe' || confirmText.trim() === podium.hardware_id

    const handleConfirm = async () => {
        if (!podium.hardware_id || !wipeReady) return
        setLoading(true)
        const hw = podium.hardware_id
        const res =
            action === 'disable'
                ? await API.ha.podium.disable(hw)
                : action === 'enable'
                  ? await API.ha.podium.enable(hw)
                  : await API.ha.podium.wipe(hw)
        setLoading(false)

        if (!res.ok) {
            enqueueSnackbar({
                variant: 'error',
                message: `Failed to ${action} ${label}: ${errorMessage(res)}`,
            })
            return
        }

        // The endpoints answer 202 with an "accepted…" note when the broker is offline: the
        // command is queued and delivered on the device's next reconnect.
        if (typeof res.value === 'string' && res.value.includes('accepted')) {
            enqueueSnackbar({
                variant: 'info',
                message: `${label} is offline — the command will apply when it reconnects.`,
            })
        } else {
            enqueueSnackbar({
                variant: 'success',
                message: `${text.past} ${label}.`,
            })
        }

        onConfirmed()
    }

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{text.title}</DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    <Typography variant="body1" gutterBottom>
                        {text.body}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {label}
                        {podium.hardware_id ? ` · ${podium.hardware_id}` : ''}
                        {podium.org_name ? ` · ${podium.org_name}` : ''}
                    </Typography>
                </DialogContentText>

                {action === 'wipe' && (
                    <>
                        <Alert severity="error" sx={{ mt: 2 }}>
                            To confirm, type the device hardware ID exactly:
                            <br />
                            <strong>{podium.hardware_id}</strong>
                        </Alert>
                        <TextField
                            fullWidth
                            size="small"
                            autoFocus
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder={podium.hardware_id ?? ''}
                            sx={{ mt: 2 }}
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <LoadingButton
                    variant="contained"
                    color={text.confirmColor}
                    loading={loading}
                    disabled={!wipeReady}
                    onClick={handleConfirm}
                >
                    {text.confirmLabel}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    )
}
